import { useState, useMemo, useCallback } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useFieldConfig } from '@/contexts/FieldConfigContext';
import type { FieldConfig, FieldState, ProjectCodeMode, ProjectCodeNumberFormat, RoleState, ProjectRoleType } from '@/contexts/FieldConfigContext';
import { PROJECT_ROLE_LABELS, PROJECT_ROLE_DISPLAY_ORDER } from '@/types';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useProjectStatuses } from '@/contexts/ProjectStatusContext';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import {
  ChevronDown,
  RotateCcw,
  CheckSquare,
  Info,
  Lock,
  Check,
  Zap,
  Pencil,
  ZapOff,
  Users,
  BookOpen,
  CalendarDays,
  Briefcase,
} from 'lucide-react';
import { toast } from 'sonner';
import { useDataMode } from '@/contexts/DataModeContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ProjectCreationSetupProps {
  onNavigate?: (page: string) => void;
}

const SECTION_META: Record<FieldConfig['section'], { label: string; description: string; icon: React.ReactNode }> = {
  essential: {
    label: 'Essential Fields',
    description: 'Core fields that are always required on every project.',
    icon: <Lock className="h-5 w-5 text-primary" />,
  },
  general: {
    label: 'General Information',
    description: 'Basic project details such as description, priority, type and status.',
    icon: <BookOpen className="h-5 w-5 text-primary" />,
  },
  timeline: {
    label: 'Timeline & Scheduling',
    description: 'Start date, end date, duration and timeline completion rules.',
    icon: <CalendarDays className="h-5 w-5 text-primary" />,
  },
  management: {
    label: 'Management & Resources',
    description: 'Key people, client, currency and budget fields.',
    icon: <Briefcase className="h-5 w-5 text-primary" />,
  },
};

// Field descriptions for tooltips
const FIELD_DESCRIPTIONS: Record<string, string> = {
  projectName: 'The unique name for your project',
  projectCode: 'A short identifier code for the project',
  description: 'Detailed description of the project scope and objectives',
  status: 'Current status of the project (e.g., Planning, Active, Completed)',
  priority: 'Priority level for resource allocation and scheduling',
  type: 'Project type or category for classification',
  startDate: 'When the project is scheduled to begin',
  endDate: 'Target completion date for the project',
  duration: 'Expected length of the project',
  projectOwner: 'Business stakeholder with ultimate accountability and budget approval authority',
  projectManager: 'Primary person responsible for day-to-day project execution and management',
  projectManagers: 'Additional project managers for large projects or sub-teams',
  clients: 'Client(s) or customer(s) for whom the project is being delivered',
  currencyCode: 'Currency for financial tracking and budget reporting',
  budget: 'Financial budget allocated to the project',
  customAttributes: 'Custom attribute fields defined in Settings → Project Settings → Custom Fields',
};



export function ProjectCreationSetup({ onNavigate }: ProjectCreationSetupProps = {}) {
  const {
    fieldConfigs,
    updateFieldState,
    defaultStatusId,
    updateDefaultStatusId,
    projectCodeSettings,
    updateProjectCodeSettings,
    generateExampleCode,
    roleConfigs,
    updateRoleState,
    hasUnsavedChanges,
    isSaving,
    isSettingsLoadError,
    saveChanges,
    resetChanges,
    resetToDefaults,
    makeAllRequired,
    makeAllOptional,
    makeAllHidden,
  } = useFieldConfig();

  const { selectableStatuses } = useProjectStatuses();
  const { isDemoMode } = useDataMode();

  // Only general open by default; rest collapsed
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    essential: false,
    general: true,
    timeline: false,
    management: false,
  });

  const [showResetDialog, setShowResetDialog] = useState(false);

  const handleFieldStateChange = (key: string, state: FieldState) => {
    const config = fieldConfigs.find(c => c.key === key);
    if (config && !config.isAlwaysRequired) {
      updateFieldState(config.key, state);
    }
  };

  const handleRoleStateChange = (role: ProjectRoleType, state: RoleState) => {
    if (role === 'project-manager') return;
    updateRoleState(role, state);
  };

  const getRoleState = (role: ProjectRoleType): RoleState => {
    const config = roleConfigs.find(c => c.role === role);
    if (!config) return 'optional';
    if (!config.enabled) return 'hidden';
    if (config.required) return 'required';
    return 'optional';
  };

  const handleSave = async () => {
    try {
      await saveChanges();
      toast.success('Settings saved successfully', {
        description: isDemoMode
          ? 'Your settings have been saved to your browser (Demo Mode — not persisted to the server).'
          : 'Your project creation setup has been updated.',
      });
    } catch (error) {
      toast.error('Failed to save settings', {
        description: 'Please try again or contact support if the issue persists.',
      });
    }
  };

  const handleReset = () => {
    resetChanges();
    toast.info('Changes discarded', {
      description: 'Reverted to last saved settings.',
    });
  };

  const handleResetToDefaults = () => {
    setShowResetDialog(true);
  };

  const confirmResetToDefaults = () => {
    resetToDefaults();
    setShowResetDialog(false);
    toast.info('Reset to defaults', {
      description: 'All settings have been reset to their default values.',
    });
  };

  const handleMakeAllRequired = useCallback(() => {
    makeAllRequired();
    toast.info('All configurable fields set to required');
  }, [makeAllRequired]);

  const handleMakeAllOptional = useCallback(() => {
    makeAllOptional();
    toast.info('All configurable fields set to optional');
  }, [makeAllOptional]);

  const handleMakeAllHidden = useCallback(() => {
    makeAllHidden();
    toast.info('All configurable fields set to hidden');
  }, [makeAllHidden]);

  const toggleSection = (section: string) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const groupedFields = useMemo(() => {
    return fieldConfigs.reduce((acc, field) => {
      if (!acc[field.section]) acc[field.section] = [];
      acc[field.section].push(field);
      return acc;
    }, {} as Record<FieldConfig['section'], FieldConfig[]>);
  }, [fieldConfigs]);

  // Reusable 3-state button group — same pattern as field rows and role rows
  const ThreeStateControl = ({
    value,
    onChange,
    ariaLabel,
    lockedAs,
  }: {
    value: FieldState;
    onChange: (state: FieldState) => void;
    ariaLabel: string;
    lockedAs?: string;
  }) => {
    if (lockedAs) {
      return (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
          <Lock className="h-3.5 w-3.5" />
          <span>{lockedAs}</span>
        </div>
      );
    }
    return (
      <div
        className="flex items-center rounded-md border overflow-hidden shrink-0"
        role="group"
        aria-label={ariaLabel}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => onChange('hidden')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border-r focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1
                ${value === 'hidden' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted/50'}`}
              aria-pressed={value === 'hidden'}
            >
              {value === 'hidden' && <Check className="h-3 w-3" />}
              Hidden
            </button>
          </TooltipTrigger>
          <TooltipContent>Not shown on the creation form</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => onChange('optional')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border-r focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1
                ${value === 'optional' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted/50'}`}
              aria-pressed={value === 'optional'}
            >
              {value === 'optional' && <Check className="h-3 w-3" />}
              Optional
            </button>
          </TooltipTrigger>
          <TooltipContent>Shown on the form but not required</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => onChange('required')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1
                ${value === 'required' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted/50'}`}
              aria-pressed={value === 'required'}
            >
              {value === 'required' && <Check className="h-3 w-3" />}
              Required
            </button>
          </TooltipTrigger>
          <TooltipContent>Must be filled before the project can be saved</TooltipContent>
        </Tooltip>
      </div>
    );
  };

  const showAutoConfig =
    projectCodeSettings.mode === 'automatic' || projectCodeSettings.mode === 'auto-override';

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Settings load error banner */}
        {isSettingsLoadError && (
          <Alert variant="destructive">
            <AlertDescription>
              Unable to load your saved settings from the server. You're currently viewing locally
              cached settings. Changes you make can still be saved.
            </AlertDescription>
          </Alert>
        )}

        {/* Always-required fields notice */}
        <div className="flex items-start gap-2 px-1 text-sm text-muted-foreground">
          <Lock className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            <strong className="text-foreground">Project Name</strong> and{' '}
            <strong className="text-foreground">Project Code</strong> are always required and cannot
            be changed.
          </span>
        </div>

        {/* Default Status — top-level setting */}
        <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Zap className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold leading-snug">Default Values</p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">Values pre-selected when a new project is created</p>
            </div>
          </div>
          <div className="border-t border-border px-6 py-5">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 shrink-0">
                <Label htmlFor="default-status" className="text-sm font-medium">
                  Default Status
                </Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <p>
                      The status pre-selected when the project creation form opens. Users can change
                      it before saving.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Select
                value={defaultStatusId !== null ? String(defaultStatusId) : '2'}
                onValueChange={(value) => updateDefaultStatusId(Number(value))}
              >
                <SelectTrigger id="default-status" className="w-full max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {selectableStatuses.map((status) => (
                    <SelectItem key={status.id} value={String(status.id)}>
                      {status.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Toolbar: Bulk Set + Reset */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Set whether each field is hidden, optional, or required on the project creation form.
          </p>
          <div className="flex items-center gap-2 shrink-0">
            {/* Bulk Set */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5">
                  <CheckSquare className="h-4 w-4" />
                  Bulk Set
                  <ChevronDown className="h-3.5 w-3.5 ml-0.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleMakeAllRequired} className="gap-2">
                  Set all configurable fields to Required
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleMakeAllOptional} className="gap-2">
                  Set all configurable fields to Optional
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleMakeAllHidden} className="gap-2">
                  Set all configurable fields to Hidden
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Reset to Defaults */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetToDefaults}
              className="gap-1.5"
            >
              <RotateCcw className="h-4 w-4" />
              Reset to Defaults
            </Button>
          </div>
        </div>

        {/* Field sections — General, Timeline, Management (no Essential) */}
        <div className="space-y-3">
          {(['general', 'timeline', 'management'] as FieldConfig['section'][])
            .filter((section) => groupedFields[section])
            .map((section) => {
              const meta = SECTION_META[section];
              return (
              <Collapsible
                key={section}
                open={openSections[section]}
                onOpenChange={() => toggleSection(section)}
              >
                <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="w-full flex items-center gap-3 px-4 py-4 hover:bg-accent/40 transition-colors text-left group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                        {meta.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold leading-snug">{meta.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{meta.description}</p>
                      </div>
                      <ChevronDown
                        className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
                          openSections[section] ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="border-t border-border divide-y divide-border">



                      {/* Field rows */}
                      {groupedFields[section]
                        .sort((a, b) => a.order - b.order)
                        .map((field) => {
                          const isAlwaysLocked = !!field.isAlwaysRequired;
                          const currentState =
                            field.fieldState ?? (field.isRequired ? 'required' : 'optional');
                          const isProjectCode = field.key === 'projectCode';

                          return (
                            <div key={field.key}>
                              <div className="flex items-center justify-between px-6 py-3 hover:bg-muted/50 transition-colors gap-4">
                                {/* Label + tooltip */}
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="text-sm font-medium truncate">{field.label}</span>
                                  {FIELD_DESCRIPTIONS[field.key] && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help shrink-0" />
                                      </TooltipTrigger>
                                      <TooltipContent side="right" className="max-w-xs">
                                        <p className="text-xs">{FIELD_DESCRIPTIONS[field.key]}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                </div>

                                {/* Control */}
                                {isAlwaysLocked ? (
                                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
                                    <Lock className="h-3.5 w-3.5" />
                                    <span>Always required</span>
                                  </div>
                                ) : (
                                  <ThreeStateControl
                                    value={currentState}
                                    onChange={(state) => handleFieldStateChange(field.key, state)}
                                    ariaLabel={`Field state for ${field.label}`}
                                  />
                                )}
                              </div>

                              {/* ── KEY PEOPLE inline panel ── */}
                              {field.key === 'people' && openSections[section] && (
                                <div className="border-t border-border bg-muted/30 px-6 py-4 space-y-3">
                                  <div className="flex items-center gap-2">
                                    <Users className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-sm font-semibold">Stakeholder Roles</span>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                      </TooltipTrigger>
                                      <TooltipContent side="right" className="max-w-xs">
                                        <p className="text-xs">
                                          Configure which roles appear in the Key People section.
                                          <br />
                                          <br />
                                          <strong>Hidden</strong> — role is not shown on the form.
                                          <br />
                                          <strong>Optional</strong> — role appears but can be left
                                          blank.
                                          <br />
                                          <strong>Required</strong> — role must be assigned before
                                          saving.
                                          <br />
                                          <br />
                                          Project Manager is always required.
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>
                                  <div className="divide-y border border-border rounded-md overflow-hidden">
                                    {PROJECT_ROLE_DISPLAY_ORDER.map((role) => {
                                      const isAlwaysRequired = role === 'project-manager';
                                      const roleState = getRoleState(role);
                                      const label = PROJECT_ROLE_LABELS[role];
                                      return (
                                        <div
                                          key={role}
                                          className="flex items-center justify-between px-3 py-2.5 bg-card hover:bg-accent/40 transition-colors gap-4"
                                        >
                                          <span className="text-sm font-medium">{label}</span>
                                          {isAlwaysRequired ? (
                                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
                                              <Lock className="h-3.5 w-3.5" />
                                              <span>Always required</span>
                                            </div>
                                          ) : (
                                            <div
                                              className="flex items-center rounded-md border overflow-hidden shrink-0"
                                              role="group"
                                              aria-label={`Role state for ${label}`}
                                            >
                                              <Tooltip>
                                                <TooltipTrigger asChild>
                                                  <button
                                                    type="button"
                                                    onClick={() => handleRoleStateChange(role, 'hidden')}
                                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border-r focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1
                                                      ${roleState === 'hidden' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted/50'}`}
                                                    aria-pressed={roleState === 'hidden'}
                                                  >
                                                    {roleState === 'hidden' && <Check className="h-3 w-3" />}
                                                    Hidden
                                                  </button>
                                                </TooltipTrigger>
                                                <TooltipContent>Role will not appear on the creation form</TooltipContent>
                                              </Tooltip>
                                              <Tooltip>
                                                <TooltipTrigger asChild>
                                                  <button
                                                    type="button"
                                                    onClick={() => handleRoleStateChange(role, 'optional')}
                                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border-r focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1
                                                      ${roleState === 'optional' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted/50'}`}
                                                    aria-pressed={roleState === 'optional'}
                                                  >
                                                    {roleState === 'optional' && <Check className="h-3 w-3" />}
                                                    Optional
                                                  </button>
                                                </TooltipTrigger>
                                                <TooltipContent>Role appears on the form but can be left blank</TooltipContent>
                                              </Tooltip>
                                              <Tooltip>
                                                <TooltipTrigger asChild>
                                                  <button
                                                    type="button"
                                                    onClick={() => handleRoleStateChange(role, 'required')}
                                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1
                                                      ${roleState === 'required' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted/50'}`}
                                                    aria-pressed={roleState === 'required'}
                                                  >
                                                    {roleState === 'required' && <Check className="h-3 w-3" />}
                                                    Required
                                                  </button>
                                                </TooltipTrigger>
                                                <TooltipContent>Role must be assigned before the project can be saved</TooltipContent>
                                              </Tooltip>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* ── PROJECT CODE inline panel ── */}
                              {isProjectCode && openSections[section] && (
                                <div className="border-t border-border bg-muted/30 px-6 py-4 space-y-4">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold">Code Generation Mode</span>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                      </TooltipTrigger>
                                      <TooltipContent side="right" className="max-w-xs">
                                        <p className="text-xs">
                                          <strong>Automatic</strong> — system generates the code,
                                          user cannot edit it.
                                          <br />
                                          <strong>Manual</strong> — user types their own project
                                          code.
                                          <br />
                                          <strong>Auto + Override</strong> — system generates a code
                                          but the user can change it.
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>

                                  <ToggleGroup
                                    type="single"
                                    variant="outline"
                                    value={projectCodeSettings.mode}
                                    onValueChange={(value) => {
                                      if (value)
                                        updateProjectCodeSettings({
                                          mode: value as ProjectCodeMode,
                                        });
                                    }}
                                    className="w-full"
                                  >
                                    <ToggleGroupItem
                                      value="automatic"
                                      className="flex-1 gap-1.5 text-xs"
                                    >
                                      <Zap className="h-3.5 w-3.5" />
                                      Automatic
                                    </ToggleGroupItem>
                                    <ToggleGroupItem
                                      value="manual"
                                      className="flex-1 gap-1.5 text-xs"
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                      Manual
                                    </ToggleGroupItem>
                                    <ToggleGroupItem
                                      value="auto-override"
                                      className="flex-1 gap-1.5 text-xs"
                                    >
                                      <ZapOff className="h-3.5 w-3.5" />
                                      Auto + Override
                                    </ToggleGroupItem>
                                  </ToggleGroup>

                                  <p className="text-xs text-muted-foreground">
                                    {projectCodeSettings.mode === 'automatic' &&
                                      'The system generates a project code automatically. Users cannot edit it.'}
                                    {projectCodeSettings.mode === 'manual' &&
                                      'Users type their own project code when creating a project.'}
                                    {projectCodeSettings.mode === 'auto-override' &&
                                      'The system generates a code, but users can override it if needed.'}
                                  </p>

                                  {showAutoConfig && (
                                    <div className="space-y-4 pt-2 border-t">
                                      <h4 className="text-sm font-semibold">Format Configuration</h4>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Prefix */}
                                        <div className="space-y-2">
                                          <div className="flex items-center gap-2">
                                            <Label htmlFor="code-prefix" className="text-sm font-medium">
                                              Prefix
                                            </Label>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                              </TooltipTrigger>
                                              <TooltipContent side="right" className="max-w-xs">
                                                <p className="text-xs">
                                                  Text prepended to every auto-generated project
                                                  code. e.g. "PROJ-" or "2025-"
                                                </p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </div>
                                          <Input
                                            id="code-prefix"
                                            value={projectCodeSettings.prefix}
                                            onChange={(e) =>
                                              updateProjectCodeSettings({ prefix: e.target.value })
                                            }
                                            placeholder="e.g. PROJ-"
                                            className="font-mono"
                                            maxLength={10}
                                          />
                                        </div>

                                        {/* Number Format */}
                                        <div className="space-y-2">
                                          <div className="flex items-center gap-2">
                                            <Label
                                              htmlFor="code-number-format"
                                              className="text-sm font-medium"
                                            >
                                              Number Format
                                            </Label>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                              </TooltipTrigger>
                                              <TooltipContent side="right" className="max-w-xs">
                                                <p className="text-xs">
                                                  How many digits the sequence number uses. e.g.
                                                  "001" = 3 digits, "0001" = 4 digits
                                                </p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </div>
                                          <Select
                                            value={projectCodeSettings.numberFormat}
                                            onValueChange={(value) =>
                                              updateProjectCodeSettings({
                                                numberFormat: value as ProjectCodeNumberFormat,
                                              })
                                            }
                                          >
                                            <SelectTrigger
                                              id="code-number-format"
                                              className="font-mono"
                                            >
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="001" className="font-mono">
                                                001 (3 digits)
                                              </SelectItem>
                                              <SelectItem value="0001" className="font-mono">
                                                0001 (4 digits)
                                              </SelectItem>
                                              <SelectItem value="00001" className="font-mono">
                                                00001 (5 digits)
                                              </SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>
                                      </div>

                                      {/* Live example */}
                                      <div className="flex items-center gap-3 p-3 rounded-md bg-background border">
                                        <span className="text-xs text-muted-foreground shrink-0">
                                          Example output:
                                        </span>
                                        <span className="font-mono text-sm font-semibold tracking-wide text-primary">
                                          {generateExampleCode()}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
              );
            })}
        </div>
      </div>

      {/* Reset to Defaults Confirmation Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to Defaults?</AlertDialogTitle>
            <AlertDialogDescription>
              This will reset all field requirements and timeline settings to their default values.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmResetToDefaults}>Reset to Defaults</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
}
